import { supabase } from './supabase';
import {
  NOTIF_EVENTS,
  eventDef,
  allowedTokenKeys,
  sampleValues,
  DEFAULT_TEMPLATES,
  renderTemplate,
  renderEmailBodyHtml,
  buildTokenValues,
  samplePayload,
  type NotifyEvent,
  type NotifyChannel,
  type NotifyPayload,
} from '../../functions/_lib/notify-templates';

/**
 * Data layer for the admin Notification console. Everything here goes straight
 * through the authed supabase client - RLS (admin/developer) is the security
 * boundary, the same pattern the rest of the admin uses (notification_settings,
 * suppressions etc. already carry admin policies). Re-exports the shared
 * template registry so the console imports it from one place.
 */

export {
  NOTIF_EVENTS,
  eventDef,
  allowedTokenKeys,
  sampleValues,
  DEFAULT_TEMPLATES,
  renderTemplate,
  renderEmailBodyHtml,
  buildTokenValues,
  samplePayload,
};
export type { NotifyEvent, NotifyChannel, NotifyPayload };

// ---- Channel toggles (notification_settings) -------------------------------
export interface NotificationSetting {
  event_type: string;
  email_enabled: boolean;
  sms_enabled: boolean;
}

export async function fetchSettings(): Promise<NotificationSetting[]> {
  const { data, error } = await supabase
    .from('notification_settings')
    .select('event_type, email_enabled, sms_enabled');
  if (error) throw error;
  return (data ?? []) as NotificationSetting[];
}

export async function setChannelEnabled(
  event_type: string,
  channel: NotifyChannel,
  enabled: boolean,
): Promise<void> {
  const patch = channel === 'email' ? { email_enabled: enabled } : { sms_enabled: enabled };
  const { error } = await supabase
    .from('notification_settings')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('event_type', event_type);
  if (error) throw error;
}

// ---- Template overrides (notification_templates) ---------------------------
export interface TemplateRow {
  event_type: string;
  channel: NotifyChannel;
  variant: string;
  subject: string | null;
  preview: string | null;
  body: string;
  cta_label: string | null;
  updated_at?: string;
}

export function templateKey(event: string, channel: string, variant: string): string {
  return `${event}:${channel}:${variant}`;
}

export async function fetchTemplateOverrides(): Promise<Map<string, TemplateRow>> {
  const { data, error } = await supabase
    .from('notification_templates')
    .select('event_type, channel, variant, subject, preview, body, cta_label, updated_at');
  if (error) throw error;
  const map = new Map<string, TemplateRow>();
  for (const r of (data ?? []) as TemplateRow[]) {
    map.set(templateKey(r.event_type, r.channel, r.variant), r);
  }
  return map;
}

export async function saveTemplate(row: TemplateRow): Promise<void> {
  const { error } = await supabase
    .from('notification_templates')
    .upsert(
      {
        event_type: row.event_type,
        channel: row.channel,
        variant: row.variant,
        subject: row.subject,
        preview: row.preview,
        body: row.body,
        cta_label: row.cta_label,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'event_type,channel,variant' },
    );
  if (error) throw error;
}

export async function resetTemplate(
  event_type: string,
  channel: NotifyChannel,
  variant: string,
): Promise<void> {
  const { error } = await supabase
    .from('notification_templates')
    .delete()
    .eq('event_type', event_type)
    .eq('channel', channel)
    .eq('variant', variant);
  if (error) throw error;
}

/** The shipped default for one (event, channel, variant) - used as the seed
 *  when no override exists, and as the "reset" target. */
export function defaultTemplate(event: NotifyEvent, channel: NotifyChannel, variant: string) {
  const ev = DEFAULT_TEMPLATES[event];
  if (channel === 'email') {
    const t = ev.email[variant] ?? ev.email.default;
    return { subject: t.subject, preview: t.preview, body: t.body, cta_label: t.cta_label, cta_target: t.cta_target };
  }
  const t = ev.sms[variant] ?? ev.sms.default;
  return { subject: null, preview: null, body: t.body, cta_label: null, cta_target: null };
}

// ---- Timing (notification_timing) ------------------------------------------
export interface TimingRow {
  ending_soon_minutes: number;
  outbid_coalesce_seconds: number;
  reminder_mid_hours: number;
  reminder_final_hours: number;
}

export async function fetchTiming(): Promise<TimingRow> {
  const { data, error } = await supabase
    .from('notification_timing')
    .select('ending_soon_minutes, outbid_coalesce_seconds, reminder_mid_hours, reminder_final_hours')
    .maybeSingle();
  if (error) throw error;
  return (data ?? {
    ending_soon_minutes: 60,
    outbid_coalesce_seconds: 45,
    reminder_mid_hours: 24,
    reminder_final_hours: 6,
  }) as TimingRow;
}

export async function saveTiming(patch: TimingRow): Promise<void> {
  const { error } = await supabase
    .from('notification_timing')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', true);
  if (error) throw error;
}

// ---- Suppressions (sms_suppressions + suppressed_emails) -------------------
export interface SuppressionRow {
  value: string;        // phone (E.164) or email
  reason: string;
  source: string | null;
  created_at: string;
}

export async function fetchSuppressions(kind: NotifyChannel): Promise<SuppressionRow[]> {
  if (kind === 'sms') {
    const { data, error } = await supabase
      .from('sms_suppressions')
      .select('phone, reason, source, created_at')
      .order('created_at', { ascending: false })
      .limit(500);
    if (error) throw error;
    return (data ?? []).map((r: any) => ({ value: r.phone, reason: r.reason, source: r.source, created_at: r.created_at }));
  }
  const { data, error } = await supabase
    .from('suppressed_emails')
    .select('email, reason, source, created_at')
    .order('created_at', { ascending: false })
    .limit(500);
  if (error) throw error;
  return (data ?? []).map((r: any) => ({ value: r.email, reason: r.reason, source: r.source, created_at: r.created_at }));
}

export async function addSuppression(kind: NotifyChannel, value: string): Promise<void> {
  if (kind === 'sms') {
    const { error } = await supabase
      .from('sms_suppressions')
      .insert({ phone: value, reason: 'manual', source: 'admin_console' });
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('suppressed_emails')
      .insert({ email: value, reason: 'unsubscribe', source: 'admin_console' });
    if (error) throw error;
  }
}

export async function removeSuppression(kind: NotifyChannel, value: string): Promise<void> {
  if (kind === 'sms') {
    const { error } = await supabase.from('sms_suppressions').delete().eq('phone', value);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('suppressed_emails').delete().eq('email', value);
    if (error) throw error;
  }
}

// ---- Delivery log (email_log + sms_log, merged) ----------------------------
export interface DeliveryRow {
  id: string;
  created_at: string;
  channel: NotifyChannel;
  event: string | null;
  recipient: string;
  status: string;
  detail: string | null;
}

export async function fetchDeliveryLog(limit = 100): Promise<DeliveryRow[]> {
  const [emails, sms] = await Promise.all([
    supabase
      .from('email_log')
      .select('id, created_at, template, recipient_email, status, detail, kind')
      .eq('kind', 'auction')
      .order('created_at', { ascending: false })
      .limit(limit),
    supabase
      .from('sms_log')
      .select('id, created_at, event_type, recipient_phone, status, detail')
      .order('created_at', { ascending: false })
      .limit(limit),
  ]);
  if (emails.error) throw emails.error;
  if (sms.error) throw sms.error;
  const rows: DeliveryRow[] = [
    ...(emails.data ?? []).map((r: any) => ({
      id: `e:${r.id}`, created_at: r.created_at, channel: 'email' as const,
      event: r.template, recipient: r.recipient_email, status: r.status, detail: r.detail,
    })),
    ...(sms.data ?? []).map((r: any) => ({
      id: `s:${r.id}`, created_at: r.created_at, channel: 'sms' as const,
      event: r.event_type, recipient: r.recipient_phone, status: r.status, detail: r.detail,
    })),
  ];
  rows.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  return rows.slice(0, limit);
}
